import { useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"

/**
 * Shared destructive-action confirmation (ZPL-334). Extracted from the
 * identical private dialogs in inspiration-card / visualization-card so every
 * irreversible delete confirms through one component. The backdrop, Escape,
 * and Cancel all close without acting — but never mid-flight (`pending`
 * gates them, or a late mutation outcome would land on unmounted state);
 * `error` (e.g. a failed mutation's message) renders inside the dialog so it
 * can stay open for a retry. Focus moves to Cancel on open and Tab cycles
 * inside the dialog — `aria-modal` must not lie about an inert background.
 */
export function ConfirmDeleteDialog({
	title,
	body,
	confirmLabel,
	cancelLabel,
	pending,
	error,
	onCancel,
	onConfirm,
}: {
	title: string
	body: string
	confirmLabel: string
	cancelLabel: string
	pending: boolean
	error?: string | null
	onCancel: () => void
	onConfirm: () => void
}) {
	const rootRef = useRef<HTMLDivElement>(null)
	const pendingRef = useRef(pending)
	pendingRef.current = pending

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !pendingRef.current) onCancel()
			if (e.key === "Tab") {
				const focusables = Array.from(
					rootRef.current?.querySelectorAll<HTMLElement>(
						"button:not([disabled])",
					) ?? [],
				)
				if (focusables.length === 0) return
				const first = focusables[0]
				const last = focusables[focusables.length - 1]
				const active = document.activeElement
				const inside = rootRef.current?.contains(active)
				if (!inside) {
					e.preventDefault()
					first.focus()
				} else if (e.shiftKey && active === first) {
					e.preventDefault()
					last.focus()
				} else if (!e.shiftKey && active === last) {
					e.preventDefault()
					first.focus()
				}
			}
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onCancel])

	return (
		<div
			ref={rootRef}
			role="alertdialog"
			aria-modal="true"
			aria-label={title}
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={cancelLabel}
				className="absolute inset-0 bg-black/60"
				onClick={() => {
					if (!pending) onCancel()
				}}
			/>
			<div className="relative w-full max-w-sm rounded-lg border border-border bg-popover text-popover-foreground p-6 shadow-2xl">
				<h2 className="text-lg font-bold text-foreground">{title}</h2>
				<p className="mt-2 text-sm text-muted-foreground">{body}</p>
				{error ? (
					<p role="alert" className="mt-2 text-sm text-destructive">
						{error}
					</p>
				) : null}
				<div className="mt-5 flex justify-end gap-2">
					<Button
						type="button"
						variant="ghost"
						// Destructive dialog: the safe action holds initial focus.
						autoFocus
						onClick={onCancel}
						disabled={pending}
					>
						{cancelLabel}
					</Button>
					<Button
						type="button"
						variant="destructive"
						onClick={onConfirm}
						disabled={pending}
					>
						{confirmLabel}
					</Button>
				</div>
			</div>
		</div>
	)
}
